const hasRole = (userRoles, role) => {
	if(Array.isArray(role)) {
		 return role.some(r => userRoles.includes('Super Admin') ||  userRoles.includes(r));
	}

  return userRoles.includes('Super Admin') ||  userRoles.includes(role)
}

const hasPermission = (userPermissions, permission) => {

	if(Array.isArray(permission)) {
		 return permission.some(p => userPermissions.includes(p));
	}

  return userPermissions.includes(permission)
}

// The company switches that put an account through the workforce approval chain: the
// timekeeper validation of the overtime, the overtime approvals, the Audit step of the
// attendance resolutions and the steps of an employee creation. The API mirrors this
// list in Company::WORKFLOW_SETTINGS, and the features that only exist inside the chain
// (deactivating the account of an employee who had no attendance for a month, the filing
// deadline of a resolution) are switched on by the same list.
const WORKFLOW_SETTING_KEYS = [
	'ot_requires_timekeeper_validation',
	'ot_requires_project_engineer_approval',
	'ot_requires_pm_approval',
	'ot_requires_construction_head_approval',
	'attendance_resolution_requires_audit_approval',
	'employee_creation_requires_pm_approval',
	'employee_creation_requires_construction_head_approval',
	'employee_creation_requires_approver_approval',
	'employee_creation_requires_audit_validation',
	'employee_creation_requires_accounting_validation',
];

/** True when one company (an entry of the companySettings getter) runs the workflow. */
const companyRunsWorkflow = (company) =>
	!!company && WORKFLOW_SETTING_KEYS.some((key) => company[key] === true);

/** True when at least one of the companies the account works on runs the workflow. */
const anyCompanyRunsWorkflow = (companySettings) =>
	Object.values(companySettings ?? {}).some((company) => companyRunsWorkflow(company));

const PERMISSION_COMPANY_SETTING_MAP = {
	'Validate Overtime': 'ot_requires_timekeeper_validation',
	'Access Attendance Verifications': 'ot_requires_timekeeper_validation',
	'Access Shifts': 'ot_requires_pm_approval',
	'Create Accounts': 'ot_requires_construction_head_approval',
	'Approve Overtime (PE)': 'ot_requires_project_engineer_approval',
	'Approve Overtime (PM)': 'ot_requires_pm_approval',
	'Approve Overtime (CH)': 'ot_requires_construction_head_approval',
	'Void Attendance': 'ot_requires_timekeeper_validation',
	'Request Attendance Resolution': 'attendance_resolution_requires_audit_approval',
	'Approve Attendance Resolution': 'attendance_resolution_requires_audit_approval',
	'Request Employee Creation': 'employee_creation_requires_pm_approval',
	'Approve Employee Creation (PM)': 'employee_creation_requires_pm_approval',
	'Approve Employee Creation (CH)': 'employee_creation_requires_construction_head_approval',
	// The approver step belongs to the account a company names (the "Approver 1"
	// right), and the two supporting validations to the Audit and Accounting roles.
	'Approve Employee Creation (Approver 1)': 'employee_creation_requires_approver_approval',
	'Validate Employee Creation (Audit)': 'employee_creation_requires_audit_validation',
	'Validate Employee Creation (Accounting)': 'employee_creation_requires_accounting_validation',
	// The salary detail changes run inside the chain as well: the requesters file a change
	// and follow it up, Accounting approves it first and Audit is the step that writes it to
	// the salary details. The value only marks the permissions as switched on by the workflow
	// companies; which switch of the nine is the one that matters is not asked, because the
	// rule below accepts any of them (the way the API does).
	'Request Salary Detail Change': 'ot_requires_timekeeper_validation',
	'Approve Salary Detail Change (Accounting)': 'ot_requires_timekeeper_validation',
	'Approve Salary Detail Change (Audit)': 'ot_requires_timekeeper_validation',
	// The employee page switches. The value only marks the permission as switched on by the
	// workflow companies; which switch of the six is the one that matters is not asked,
	// because the rule below accepts any of them (like the API does).
	'Deactivate Employee Account': 'ot_requires_timekeeper_validation',
	'Reactivate Employee Account': 'ot_requires_timekeeper_validation',
};

const hasCompanyPermission = (userPermissions, userRoles, companySettings, permission) => {
	if (Array.isArray(permission)) {
		return permission.some((p) => hasCompanyPermission(userPermissions, userRoles, companySettings, p));
	}
	if (hasPermission(userPermissions, permission)) {
	  if (PERMISSION_COMPANY_SETTING_MAP[permission]) {
	    const companies = Object.values(companySettings);
	    if (!companies.length) return true;
	    return anyCompanyRunsWorkflow(companySettings);
	  }
	  return true;
	}
	return false;
};

export { hasRole , hasPermission, hasCompanyPermission, PERMISSION_COMPANY_SETTING_MAP, WORKFLOW_SETTING_KEYS, companyRunsWorkflow, anyCompanyRunsWorkflow }